import List from '@bridebook/web/app-basic/components/articles/article-list/list';
import ArticlesToolHeader from '@bridebook/web/app-basic/components/articles/article-tool-header/article-tool-header';
import ArticleCategoriesList from '@bridebook/web/app-basic/components/articles/article-categories-list';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction } from 'react';
import { IStrippedArticlePostTag } from '@bridebook/toolbox/src/schemas/article/types';
import { Box, Container } from '@bridebook/ui';
import ArticlePagination from '@bridebook/web/app-shared/components/article-pagination/article-pagination';
import { IArticle } from '@bridebook/web/app-shared/lib/articles/types';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { ARTICLES_PER_PAGE } from '@bridebook/web/lib/articles/constants/constants';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { Flex, Text } from '@bridebook/aura';
import ArticlesAppBanner from '@bridebook/web/app-basic/components/articles/articles-app-banner/articles-app-banner';
import { useCategoryDescriptions } from '@bridebook/web/app-shared/lib/articles/hooks/use-category-descriptions';
import { useArticleCategoryName } from '@bridebook/web/app-shared/lib/articles/hooks/use-article-category-name';
import AppAdComponent from '@bridebook/web/app-shared/components/articles/app-ad/app-ad';

interface IProps {
  articles?: IArticle[];
  articlesLoaded: boolean;
  recordsFound?: number;
  isBookmarksView?: boolean;
  loadMore?: () => void;
  expectedCount?: number;
  articleCategory: IStrippedArticlePostTag | null;
  activePage: number;
  handleOnSelect: (item: { selected: number }) => void;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

const ArticlesPage = ({
  articles,
  articlesLoaded,
  recordsFound,
  isBookmarksView,
  expectedCount,
  activePage,
  handleOnSelect,
  articleCategory,
  setCurrentPage,
}: IProps) => {
  const { query } = useRouter();
  const market = useMarket();
  const category = query.category?.[0] as string;
  const isMobile = useSelector(getIsMobile);

  const handleHrefBuilder = (index: number) =>
    `/${market.prefix}${UrlHelper.advice}${category ? `/${category}` : ''}?page=${index}`;

  //Divided by 7 so only 7 records will be shown
  const totalItems = Math.floor(Number((recordsFound || ARTICLES_PER_PAGE) / ARTICLES_PER_PAGE));

  const categoryDescriptions = useCategoryDescriptions(category);
  const categoryName = useArticleCategoryName(category);

  return (
    <>
      <Container mb={6} noPadding>
        {!isBookmarksView && (
          <Box mx={[6, 0]} mb={4} mt={[6, 0]}>
            <ArticlesToolHeader
              title={categoryName || articleCategory?.name}
              description={categoryDescriptions?.hero || articleCategory?.description}
            />
          </Box>
        )}
        <Flex className="flex-col gap-10 md:flex-row">
          <div className="flex-1">
            <Box mx={[6, 0]}>
              <List
                articles={articles}
                articlesLoaded={articlesLoaded}
                expectedCount={expectedCount}
                isBookmarksView={isBookmarksView}
              />
              {!!articles && !!recordsFound && recordsFound > 7 && (
                <ArticlePagination
                  items={totalItems}
                  activePage={activePage - 1}
                  isMobile={isMobile}
                  onSelect={handleOnSelect}
                  hrefBuilder={handleHrefBuilder}
                />
              )}
            </Box>
          </div>
          {articlesLoaded && (
            <div className="mx-6 -mt-16 md:mx-0 md:mt-0 md:w-[280px]">
              <ArticleCategoriesList
                isBookmarksView={isBookmarksView}
                activeSlug={articleCategory?.slug}
                setCurrentPage={setCurrentPage}
              />
            </div>
          )}
        </Flex>
        {!isMobile && categoryDescriptions?.bottom && (
          <Text className="my-6 whitespace-pre-wrap">{categoryDescriptions.bottom}</Text>
        )}
        {!isMobile && categoryDescriptions?.faq && (
          <Text className="my-6 whitespace-pre-wrap">{categoryDescriptions.faq}</Text>
        )}
        <div className="mx-2 sm:mx-0 sm:mt-8">
          {isMobile ? <AppAdComponent isListView /> : <ArticlesAppBanner />}
        </div>
      </Container>
    </>
  );
};

export default ArticlesPage;
